import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getReferralActionRequiredCountEndPoint } from '../../common/api.endPoints';
import { HttpService } from '../../common/http.service';

export const REFERRAL_ACTION_REQUIRED_COUNT_QUERY_KEY = [
  'referral-action-required-count',
];

// localStorage key for the cross-tab cached count. Persisting the value (with a
// last-refreshed timestamp) lets every tab share one cache: a tab only calls the
// API once the stored value has expired, instead of each tab refreshing on its
// own timer.
const REFERRAL_COUNT_STORAGE_KEY = 'nora.referralActionRequiredCount';

// How long a stored count stays valid before a tab re-fetches.
const CACHE_VALIDITY_MS = 15 * 60 * 1000; // 15 minutes

// react-query is still the in-tab cache: shared query key dedupes consumers and
// staleTime stops navigation from re-fetching. The poll only triggers a network
// call when the localStorage value is also expired.
const STALE_TIME_MS = 60 * 1000;
const REFETCH_INTERVAL_MS = CACHE_VALIDITY_MS;

interface StoredReferralCount {
  count: number;
  lastRefreshedAt: number;
}

function readStoredCount(): StoredReferralCount | null {
  try {
    const raw = window.localStorage.getItem(REFERRAL_COUNT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<StoredReferralCount>;
    if (
      typeof parsed?.count === 'number' &&
      typeof parsed?.lastRefreshedAt === 'number'
    ) {
      return { count: parsed.count, lastRefreshedAt: parsed.lastRefreshedAt };
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredCount(count: number): void {
  try {
    window.localStorage.setItem(
      REFERRAL_COUNT_STORAGE_KEY,
      JSON.stringify({ count, lastRefreshedAt: Date.now() }),
    );
  } catch {
    // Ignore storage write failures (e.g. private mode / quota) and fall back to
    // the in-memory react-query cache.
  }
}

function isStoredCountValid(stored: StoredReferralCount): boolean {
  return Date.now() - stored.lastRefreshedAt < CACHE_VALIDITY_MS;
}

// Returns the parsed count, or null when the request failed. Returning null
// (instead of 0) lets the caller avoid caching a failure as a real "0 referrals".
async function fetchReferralActionRequiredCount(): Promise<number | null> {
  const response = await HttpService.getInstance().get(
    getReferralActionRequiredCountEndPoint(),
  );
  if (response?.error) {
    return null;
  }
  const body = response?.parsedBody as { count?: number } | undefined;
  const raw = body?.count;
  return Number.isFinite(raw) ? (raw as number) : 0;
}

async function getReferralActionRequiredCount(): Promise<number> {
  const stored = readStoredCount();
  if (stored && isStoredCountValid(stored)) {
    return stored.count;
  }
  const count = await fetchReferralActionRequiredCount();
  if (count === null) {
    // Don't cache failures — keep the last known value (or 0) so a transient
    // error doesn't hide the badge for the whole validity window.
    return stored?.count ?? 0;
  }
  writeStoredCount(count);
  return count;
}

export function useReferralActionRequiredCount(enabled = true): {
  count: number;
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const queryClient = useQueryClient();

  // Seed from a still-valid stored value so the badge shows the cached count
  // immediately (no loading flash) without waiting for the async queryFn.
  const initialStored = useMemo(() => readStoredCount(), []);
  const hasValidInitial = Boolean(
    initialStored && isStoredCountValid(initialStored),
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: REFERRAL_ACTION_REQUIRED_COUNT_QUERY_KEY,
    queryFn: getReferralActionRequiredCount,
    enabled,
    staleTime: STALE_TIME_MS,
    cacheTime: Infinity,
    refetchInterval: enabled ? REFETCH_INTERVAL_MS : false,
    refetchOnWindowFocus: false,
    initialData: hasValidInitial ? initialStored?.count : undefined,
    initialDataUpdatedAt: hasValidInitial
      ? initialStored?.lastRefreshedAt
      : undefined,
  });

  // Keep this tab in sync when another tab refreshes the stored count.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== REFERRAL_COUNT_STORAGE_KEY) {
        return;
      }
      const stored = readStoredCount();
      if (stored) {
        queryClient.setQueryData(
          REFERRAL_ACTION_REQUIRED_COUNT_QUERY_KEY,
          stored.count,
        );
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [queryClient]);

  return {
    count: data ?? 0,
    loading: enabled && isLoading,
    refetch: async () => {
      await refetch();
    },
  };
}
